import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';
import storage from '../utils/storage';
import { ApiResponse } from '../types';

/**
 * API 客户端类
 */
class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config) => {
        // 添加 token
        const token = await storage.getItem<string>(STORAGE_KEYS.TOKEN);
        if (token) {
          config.headers.token = token;
        }
        
        console.log('Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        console.log('Response:', response.config.url, response.data);
        
        // 处理 401 未授权
        if (response.data.code === 401) {
          this.handleUnauthorized();
          return Promise.reject(new Error(response.data.msg || '请先登录'));
        }

        // 返回 data
        return response.data as any;
      },
      (error) => {
        console.error('Response error:', error);
        
        // 网络错误
        if (!error.response) {
          return Promise.reject(new Error('网络连接失败，请检查网络设置'));
        }

        // 服务器错误
        const { status, data } = error.response;
        if (status === 401) {
          this.handleUnauthorized();
          return Promise.reject(new Error('登录已过期，请重新登录'));
        }

        return Promise.reject(new Error(data?.msg || '请求失败，请稍后重试'));
      }
    );
  }

  /**
   * 处理未授权
   */
  private async handleUnauthorized() {
    // 清除 token
    await storage.removeItem(STORAGE_KEYS.TOKEN);
    
    // TODO: 跳转到登录页
    // 这里需要在导航配置好后实现
    console.warn('用户未授权，需要重新登录');
  }

  /**
   * GET 请求
   */
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.get(url, config);
  }

  /**
   * POST 请求
   */
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.post(url, data, config);
  }

  /**
   * PUT 请求
   */
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.put(url, data, config);
  }

  /**
   * DELETE 请求
   */
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.delete(url, config);
  }

  /**
   * 上传文件
   */
  upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.instance.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    });
  }
}

export default new ApiClient();
